// ThingsToDo.jsx
import React from 'react';
import { useTranslation } from 'react-i18next'; // Added
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// === HERO BACKGROUND ===
import heroBg from '../assets/pick/1.jpg';

// === ACTIVITY IMAGES ===
import ayurveda from '../assets/ThingsToDo/Ayurvedic-Therapies-For-Stress-Relief.webp';
import diving from '../assets/ThingsToDo/unadiving1.jpg';
import elephantRide from '../assets/ThingsToDo/elephant-back-riding.jpg';
import fishing from '../assets/ThingsToDo/S4.jpg';
import ballooning from '../assets/ThingsToDo/hot-air-baloon.jpg';
import jeepSafari from '../assets/ThingsToDo/2409696_orig.jpg';
import whiteWaterRafting from '../assets/ThingsToDo/rafting7.jpg';
import riverSafari from '../assets/ThingsToDo/river-safari-slider1.jpg';
import snorkeling from '../assets/ThingsToDo/Snorkelers_2.jpg';
import teaTasting from '../assets/ThingsToDo/Tea-Tasting.avif';
import templeVisit from '../assets/ThingsToDo/Buddhist-Temples-in-Sri-Lanka.jpg';
import wildlifeTour from '../assets/ThingsToDo/protect-the-environment-on-your-sri-lanka-wildlife-safari-header.jpg';

// === MOTION VARIANTS ===
const sectionVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: 'easeOut' } },
};

const ThingsToDo = () => {
    const { t } = useTranslation('thingstodo'); // Use thingstodo namespace
    const navigate = useNavigate();

    const activities = [
        { image: ayurveda, title: t('ayurveda'), route: '/ayurveda' },
        { image: diving, title: t('diving'), route: '/diving' },
        { image: elephantRide, title: t('elephantRide'), route: '/elephant-ride' },
        { image: fishing, title: t('fishing'), route: '/fishing' },
        { image: ballooning, title: t('ballooning'), route: '/ballooning' },
        { image: jeepSafari, title: t('jeepSafari'), route: '/jeep-safari' },
        { image: whiteWaterRafting, title: t('whiteWaterRafting'), route: '/white-water-rafting' },
        { image: riverSafari, title: t('riverSafari'), route: '/river-safari' },
        { image: snorkeling, title: t('snorkeling'), route: '/snorkeling' },
        { image: teaTasting, title: t('teaTasting'), route: '/tea-tasting' },
        { image: templeVisit, title: t('templeVisit'), route: '/temple-visit' },
        { image: wildlifeTour, title: t('wildlifeSafari'), route: '/wildlife-safari' },
    ];

    return (
        <div className="bg-[#0a0a0a] min-h-screen">
            {/* HERO SECTION */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative flex h-[60vh] w-full items-center justify-center font-lato md:h-[50vh] lg:h-[80vh] overflow-hidden"
            >
                <img
                    src={heroBg}
                    alt={t('heroTitle')}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/60 to-black/85" />

                <div className="container relative z-10 flex flex-col items-center justify-center pt-[80px] px-4">
                    <h1 className="text-center text-[50px] font-bold leading-[70px] text-white font-konkhmer md:text-[60px] lg:text-[80px]">
                        <span
                            style={{
                                background: 'linear-gradient(to right, #166880, #D5A44F)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                textShadow: '0 0 15px rgba(22, 104, 128, 0.4)',
                            }}
                        >
                            {t('heroTitle')}
                        </span>
                    </h1>
                    <p className="mt-6 max-w-[90%] text-center font-inknut text-lg font-medium text-white/90 sm:mt-10 sm:max-w-3xl sm:text-2xl">
                        {t('heroSubtitle')}
                    </p>

                    {/* Gradient Corner SVGs */}
                    <div className="absolute left-6 top-10 md:left-10 md:top-10">
                        <svg width="340" height="300" viewBox="0 0 340 300" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M340 1H1V299.5" stroke="url(#gradLeft)" strokeWidth="1.8" />
                            <defs>
                                <linearGradient id="gradLeft" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#166880" />
                                    <stop offset="100%" stopColor="#D5A44F" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                    <div className="absolute -bottom-12 right-6 md:-bottom-16 md:right-10">
                        <svg width="340" height="300" viewBox="0 0 340 300" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 299.5H339V0" stroke="url(#gradRight)" strokeWidth="1.8" />
                            <defs>
                                <linearGradient id="gradRight" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#D5A44F" />
                                    <stop offset="100%" stopColor="#166880" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                </div>
            </motion.div>

            {/* ACTIVITIES SECTION */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="py-16 lg:py-24"
            >
                <div className="container mx-auto px-4">
                    <div className="text-center">
                        <h2 className="font-konkhmer text-4xl font-bold uppercase md:text-5xl lg:text-6xl">
                            <span
                                style={{
                                    background: 'linear-gradient(to right, #166880, #D5A44F)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                    textShadow: '0 0 12px rgba(213, 164, 79, 0.3)',
                                }}
                            >
                                {t('sectionTitle')}
                            </span>
                        </h2>
                        <div className="mx-auto mt-4 h-[4px] w-28 bg-gradient-to-r from-[#166880] to-transparent rounded-l-full"></div>
                        <div className="mx-auto mt-1 h-[4px] w-20 bg-gradient-to-r from-transparent to-[#D5A44F] rounded-r-full"></div>
                    </div>

                    <div className="mt-14 flex w-full items-stretch gap-5 overflow-x-auto px-4 pb-6 md:grid md:grid-cols-3 md:pb-0 lg:mt-20 lg:gap-6 lg:px-6 xl:grid-cols-4">
                        {activities.map((item, idx) => (
                            <motion.div
                                key={idx}
                                whileHover={{ scale: 1.03, y: -8 }}
                                whileTap={{ scale: 0.98 }}
                                className="group relative h-[620px] w-[340px] shrink-0 overflow-hidden rounded-[20px] md:h-[300px] md:w-auto lg:h-[500px] xl:h-[680px] cursor-pointer shadow-xl transition-all duration-500"
                                onClick={() => navigate(item.route)}
                            >
                                <img
                                    src={item.image}
                                    alt={item.title}
                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"></div>

                                <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center px-5 py-8 text-center">
                                    <h4 className="font-redHatDisplay text-2xl font-bold text-white drop-shadow-2xl md:text-lg lg:text-2xl xl:text-3xl">
                                        {item.title}
                                    </h4>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(item.route);
                                        }}
                                        className="mt-5 rounded-full px-7 py-3 font-bold text-white text-sm md:text-base transition-all duration-300 shadow-lg hover:scale-105"
                                        style={{
                                            background: 'linear-gradient(to right, #166880, #D5A44F)',
                                            boxShadow: '0 6px 20px rgba(22, 104, 128, 0.4)',
                                        }}
                                        onMouseEnter={(e) => e.target.style.background = 'linear-gradient(to right, #0f4d5e, #b88a3a)'}
                                        onMouseLeave={(e) => e.target.style.background = 'linear-gradient(to right, #166880, #D5A44F)'}
                                    >
                                        {t('viewMore')}
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.section>
        </div>
    );
};

export default ThingsToDo;